// Bearer-token authenticated axios wrapper.
// Cookies (HttpOnly) are sent automatically by the browser — the BFF injects
// Authorization: Bearer server-side for every /api/* request.
// On 401, the session is refreshed via POST /auth/refresh (BFF) before retrying.
// On network errors and 502/503 (BFF restart during rolling update), the request
// is retried with exponential backoff (1 s → 2 s → 4 s → 8 s, up to 4 attempts).
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';

import { refresh } from './authentication';

const TRANSIENT_RETRY_BASE_DELAY_MS = 1000;
const TRANSIENT_RETRY_MAX_ATTEMPTS = 4;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const response = (err as { response?: { status?: number } }).response;
  if (!response) return true; // network error / connection reset (BFF died)
  return response.status === 502 || response.status === 503;
}

async function withTransientRetry<T>(fn: () => Promise<T>): Promise<T> {
  for (let attempt = 0; attempt < TRANSIENT_RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (!isTransientError(err) || attempt === TRANSIENT_RETRY_MAX_ATTEMPTS - 1) throw err;
      await sleep(TRANSIENT_RETRY_BASE_DELAY_MS * 2 ** attempt);
    }
  }
  throw new Error('unreachable');
}

async function authenticatedGet<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  try {
    return await withTransientRetry(() => axios.get<T>(url, config));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response?.status === 401) {
      await refresh();
      return await withTransientRetry(() => axios.get<T>(url, config));
    }
    throw err;
  }
}

async function authenticatedPost<T>(
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  try {
    return await withTransientRetry(() => axios.post<T>(url, data, config));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response?.status === 401) {
      await refresh();
      return await withTransientRetry(() => axios.post<T>(url, data, config));
    }
    throw err;
  }
}

async function authenticatedPut<T>(
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  try {
    return await withTransientRetry(() => axios.put<T>(url, data, config));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response?.status === 401) {
      await refresh();
      return await withTransientRetry(() => axios.put<T>(url, data, config));
    }
    throw err;
  }
}

async function authenticatedDelete<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  try {
    return await withTransientRetry(() => axios.delete<T>(url, config));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (err.response?.status === 401) {
      await refresh();
      return await withTransientRetry(() => axios.delete<T>(url, config));
    }
    throw err;
  }
}

export default {
  get: authenticatedGet,
  post: authenticatedPost,
  put: authenticatedPut,
  delete: authenticatedDelete,
};
